import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, CreditCard, Receipt, Repeat, History, Trophy, Settings as SettingsIcon, HelpCircle, Sparkles, Send, Search as SearchIcon, TrendingUp, Clock, Info, ShieldCheck, FileText, Cookie, Zap } from 'lucide-react';
import { useOnboarding } from '../onboarding/OnboardingContext';
import { useCredits } from '../hooks/useCredits';
import PricingModal from './PricingModal';
import QuickTopUp from './QuickTopUp';

// ---------- Reusable overlay shell (dark olive glassmorphism) ----------
function OverlayShell({ open, onClose, title, children, size = 'md', testId }) {
    if (!open) return null;
    const width = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' }[size] || 'max-w-xl';
    return createPortal(
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[340] flex items-center justify-center p-4"
            style={{ background: 'rgba(11,15,7,0.72)', backdropFilter: 'blur(6px)' }}
            onClick={onClose}
            data-testid={testId}
        >
            <motion.div
                initial={{ y: 24, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }}
                onClick={(e) => e.stopPropagation()}
                className={`w-full ${width} max-h-[90vh] rounded-lg overflow-hidden flex flex-col border border-[#3e4438] bg-[#1a1e17]/90 backdrop-blur-xl`}
                style={{
                    boxShadow: '0 40px 80px -25px rgba(0,0,0,0.75)',
                }}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#3e4438]">
                    <div className="display text-[#f0f4ee] text-xl">{title}</div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-sm text-[#c2ccbe] hover:text-[#f0f4ee] hover:bg-white/10 transition-colors"
                        aria-label="Close"
                        data-testid={testId ? `${testId}-close` : undefined}
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto book-scroll px-6 py-5">
                    {children}
                </div>
            </motion.div>
        </motion.div>,
        document.body,
    );
}

// ---------- Legal / About content ----------
const LEGAL_COPY = {
    about: {
        title: 'About Studyfolio',
        body: (
            <div className="space-y-4 text-[14px] text-[#c2ccbe] leading-relaxed">
                <p><strong className="text-[#f0f4ee]">Studyfolio</strong> is an AI-driven study ecosystem crafted for Indian students preparing for boards and entrance exams. We combine the tactile joy of a paper journal with the intelligence of a Socratic AI mentor — <em>Metis</em> — to make everyday revision feel like turning the page of a beautifully bound book.</p>
                <p>Our mission is simple: <strong className="text-[#f0f4ee]">make disciplined study feel personal, not punishing.</strong> Every chapter card, every quiz, and every credit is tuned to the student's syllabus, time budget, and objective.</p>
                <p>Studyfolio is proudly built in <strong className="text-[#f0f4ee]">Hyderabad, Telangana</strong> — from a small studio that believes learning tools should be as elegant as the ideas they carry.</p>
                <p className="mono text-[11px] text-[#8a957f]">© MMXXVI Folio &amp; Co. · Hyderabad, Telangana, India</p>
            </div>
        ),
    },
    privacy: {
        title: 'Privacy Policy',
        body: (
            <div className="space-y-3 text-[13px] text-[#c2ccbe] leading-relaxed">
                <p><strong className="text-[#f0f4ee]">Effective date:</strong> July 2026</p>
                <p>We collect only the information needed to make your Studyfolio experience personal: your name, email (or phone), CBSE class and focus subjects, and your interaction history with Metis.</p>
                <p><strong className="text-[#f0f4ee]">How we use it:</strong> to tune AI responses, track credits, and improve product features. We do not sell your data to third parties.</p>
                <p><strong className="text-[#f0f4ee]">Third-party sub-processors:</strong> secure cloud database, payment processing gateways, single sign-on authentication providers, and AI language model infrastructure.</p>
                <p><strong className="text-[#f0f4ee]">Your rights:</strong> you may request export or deletion of your data at any time by writing to us at hello@studyfolio.in.</p>
                <p><strong className="text-[#f0f4ee]">Retention:</strong> chat transcripts are stored for 12 months and then anonymised. Account records are retained while your account is active.</p>
                <p><strong className="text-[#f0f4ee]">Children:</strong> Studyfolio is designed for students aged 13+. Parents may request removal for minor users.</p>
            </div>
        ),
    },
    terms: {
        title: 'Terms & Conditions',
        body: (
            <div className="space-y-3 text-[13px] text-[#c2ccbe] leading-relaxed">
                <p><strong className="text-[#f0f4ee]">Effective date:</strong> July 2026</p>
                <p><strong className="text-[#f0f4ee]">Account:</strong> you are responsible for keeping your login credentials secure. One human per account.</p>
                <p><strong className="text-[#f0f4ee]">Credits &amp; subscriptions:</strong> Metis actions (chat, lessons, quizzes) each consume 1 credit. Credit packs and subscription plans auto-renew until cancelled from the Manage Subscriptions panel. Refunds follow Indian consumer law and are considered case-by-case within 7 days of purchase.</p>
                <p><strong className="text-[#f0f4ee]">Content:</strong> AI-generated study material is a supplement, not a replacement, for official NCERT / board texts. Studyfolio makes best-effort accuracy but cannot guarantee exam outcomes.</p>
                <p><strong className="text-[#f0f4ee]">Acceptable use:</strong> no automated scraping, no re-selling of AI outputs, no abusive or unlawful content submissions.</p>
                <p><strong className="text-[#f0f4ee]">Termination:</strong> we may suspend accounts that violate these terms. You may terminate anytime; unused credits are non-refundable after 7 days.</p>
                <p><strong className="text-[#f0f4ee]">Governing law:</strong> These terms are governed by the laws of India. Disputes resolve in Hyderabad, Telangana courts.</p>
            </div>
        ),
    },
    cookies: {
        title: 'Cookie Policy',
        body: (
            <div className="space-y-3 text-[13px] text-[#c2ccbe] leading-relaxed">
                <p><strong className="text-[#f0f4ee]">Effective date:</strong> July 2026</p>
                <p>Studyfolio uses cookies (and localStorage) to keep you signed in, remember your onboarding preferences, and preserve your Metis conversation across visits.</p>
                <p><strong className="text-[#f0f4ee]">Essential:</strong> session cookie (auth), localStorage keys for onboarding profile, Metis chat history, credit balance.</p>
                <p><strong className="text-[#f0f4ee]">Analytics:</strong> we may use privacy-friendly analytics to understand aggregate usage. No individual-level tracking.</p>
                <p><strong className="text-[#f0f4ee]">Advertising:</strong> we currently do not run behavioural ad networks. If we add them later, this policy will be updated with clear opt-in.</p>
                <p>You can clear cookies from your browser settings at any time — you'll be asked to sign in again.</p>
            </div>
        ),
    },
};

export function LegalModal({ kind, open, onClose }) {
    const doc = LEGAL_COPY[kind];
    if (!doc) return null;
    return (
        <OverlayShell open={open} onClose={onClose} title={doc.title} size="md" testId={`legal-${kind}`}>
            {doc.body}
        </OverlayShell>
    );
}

// ---------- Account panel (Pricing / Invoices / Subs / History / Gamification) ----------
export function AccountMenu({ open, onClose }) {
    const { user } = useOnboarding();
    const { credits } = useCredits();
    const [buyOpen, setBuyOpen] = useState(false);
    const [topUpOpen, setTopUpOpen] = useState(false);
    const [tab, setTab] = useState('pricing');

    // Mock data (real endpoints exist under /api/credits/history for purchase log)
    const invoices = useMemo(() => JSON.parse(localStorage.getItem('sf.invoices.v1') || '[]'), [open]);
    const subs = useMemo(() => JSON.parse(localStorage.getItem('sf.subs.v1') || '[]'), [open]);
    const xp = user?.xp || parseInt(localStorage.getItem('sf.xp') || '120', 10);
    const level = Math.max(1, Math.floor(xp / 100));
    const badge = user?.badge || (xp >= 500 ? 'Scholar' : xp >= 200 ? 'Apprentice' : 'Curious Reader');

    const tabs = [
        { id: 'pricing', label: 'Pricing', icon: <CreditCard size={13} /> },
        { id: 'invoices', label: 'Invoices', icon: <Receipt size={13} /> },
        { id: 'subs', label: 'Manage Subscriptions', icon: <Repeat size={13} /> },
        { id: 'history', label: 'Purchase History', icon: <History size={13} /> },
        { id: 'gamification', label: 'XP & Level', icon: <Trophy size={13} /> },
    ];

    return (
        <>
            <OverlayShell open={open} onClose={onClose} title="My Account" size="lg" testId="account-menu">
                <div className="flex flex-wrap gap-2 mb-5">
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`account-tab-${t.id}`}
                            className={`px-3 py-1.5 rounded-full mono text-[11px] flex items-center gap-1.5 border transition-colors ${
                                tab === t.id
                                    ? 'bg-[#B79CFF]/20 text-[#E9DFFF] border-[#B79CFF]/50'
                                    : 'border-[#3e4438] text-[#c2ccbe] hover:bg-white/10 hover:text-[#f0f4ee]'
                            }`}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>

                {tab === 'pricing' && (
                    <div className="text-center py-6">
                        <div className="mono text-[10px] tracking-[0.24em] text-[#CBB4FF] mb-2">CURRENT BALANCE</div>
                        <div className="display text-4xl text-[#f0f4ee]" data-testid="account-credits">{credits} credits</div>
                        <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
                            <button
                                onClick={() => setBuyOpen(true)}
                                data-testid="account-buy-more"
                                className="inline-flex items-center gap-2 mono text-[12px] font-semibold text-[color:var(--moss-dark)] px-5 py-3 rounded-sm"
                                style={{ background: 'var(--lavender)' }}
                            >
                                <Sparkles size={13} /> Buy more credits
                            </button>
                            <button
                                onClick={() => setTopUpOpen(true)}
                                data-testid="account-quick-topup"
                                className="inline-flex items-center gap-2 mono text-[12px] px-5 py-3 rounded-sm border border-[#3e4438] text-[#c2ccbe] hover:bg-white/10 hover:text-[#f0f4ee] transition-colors"
                            >
                                <Zap size={13} /> Quick UPI top-up
                            </button>
                        </div>
                    </div>
                )}
                {tab === 'invoices' && (
                    <div>
                        {invoices.length === 0 ? (
                            <div className="text-[13px] text-[#c2ccbe] italic">No invoices yet — purchases will appear here with GST-compliant receipts.</div>
                        ) : (
                            <ul className="divide-y divide-[#3e4438]">
                                {invoices.map((inv, i) => (
                                    <li key={i} className="py-3 flex items-center justify-between">
                                        <div>
                                            <div className="text-[13px] text-[#f0f4ee]">{inv.title}</div>
                                            <div className="mono text-[10px] text-[#8a957f]">{inv.date} · {inv.number}</div>
                                        </div>
                                        <div className="mono text-[13px] tabular-nums text-[#f0f4ee]">₹{inv.amount}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {tab === 'subs' && (
                    <div>
                        {subs.length === 0 ? (
                            <div className="text-[13px] text-[#c2ccbe] italic">No active subscriptions. Choose a shelf under <strong className="text-[#f0f4ee]">Pricing</strong> to subscribe monthly or annually.</div>
                        ) : (
                            <ul className="space-y-3">
                                {subs.map((s, i) => (
                                    <li key={i} className="border border-[#3e4438] rounded-sm p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-[14px] text-[#f0f4ee]">{s.plan}</div>
                                                <div className="mono text-[10px] text-[#8a957f]">Renews {s.renews}</div>
                                            </div>
                                            <button className="mono text-[10px] text-red-400 hover:underline">Cancel</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {tab === 'history' && (
                    <PurchaseHistory />
                )}
                {tab === 'gamification' && (
                    <div className="text-center py-6">
                        <div className="mono text-[10px] tracking-[0.24em] text-[#CBB4FF] mb-2">METIS LEVEL</div>
                        <div className="display text-5xl text-[#f0f4ee]" data-testid="xp-level">Lvl {level}</div>
                        <div className="mono text-[11px] mt-1 text-[#c2ccbe]">{xp} XP · {badge}</div>
                        <div className="mt-4 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-[#B79CFF]" style={{ width: `${Math.min(100, xp % 100)}%` }} />
                        </div>
                        <div className="mono text-[9px] text-[#8a957f] mt-2">{100 - (xp % 100)} XP to next level</div>
                    </div>
                )}
            </OverlayShell>
            <PricingModal open={buyOpen} onClose={() => setBuyOpen(false)} onPurchased={() => setBuyOpen(false)} />
            <QuickTopUp open={topUpOpen} onClose={() => setTopUpOpen(false)} />
        </>
    );
}

function PurchaseHistory() {
    const [entries, setEntries] = useState(null);
    useEffect(() => {
        // Reads from /api/credits/history if authed; otherwise falls back to local ledger.
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        fetch(`${API}/credits/history?limit=25`, { credentials: 'include' })
            .then((r) => r.ok ? r.json() : { entries: [] })
            .then((d) => setEntries(d.entries || []))
            .catch(() => setEntries([]));
    }, []);
    if (entries === null) return <div className="mono text-[10px] text-[#8a957f]">Loading…</div>;
    if (!entries.length) return <div className="text-[13px] italic text-[#c2ccbe]">No purchases yet — Metis actions and top-ups will show up here in chronological order.</div>;
    return (
        <ul className="divide-y divide-[#3e4438]">
            {entries.map((e, i) => (
                <li key={i} className="py-2.5 flex items-center justify-between text-[13px]">
                    <div>
                        <div className="text-[#f0f4ee]">{e.reason}</div>
                        <div className="mono text-[10px] text-[#8a957f]">{e.at?.slice(0, 19).replace('T', ' ')}</div>
                    </div>
                    <div className={`mono tabular-nums ${e.delta > 0 ? 'text-emerald-400' : 'text-[#c2ccbe]'}`}>
                        {e.delta > 0 ? '+' : ''}{e.delta}
                    </div>
                </li>
            ))}
        </ul>
    );
}

// ---------- Settings + Help Chatbot ----------
export function SettingsPanel({ open, onClose }) {
    const { user, reset } = useOnboarding();
    return (
        <OverlayShell open={open} onClose={onClose} title="Settings" size="md" testId="settings-panel">
            <div className="space-y-4 text-[13px] text-[#c2ccbe]">
                <div>
                    <div className="mono text-[10px] tracking-[0.2em] text-[#8a957f] mb-1">PROFILE</div>
                    <div className="text-[#f0f4ee]">{user?.name || 'Guest'} · <span className="text-[#c2ccbe]">{user?.email || 'not signed in'}</span></div>
                    <div className="mono text-[10px] text-[#8a957f] mt-1">{user?.board_class || 'CBSE Class 10'} · {(user?.focus_areas || []).join(' · ') || 'no subjects set'}</div>
                </div>
                <div>
                    <div className="mono text-[10px] tracking-[0.2em] text-[#8a957f] mb-1">PREFERENCES</div>
                    <label className="flex items-center gap-2 mt-2"><input type="checkbox" defaultChecked /> Voice replies from Metis</label>
                    <label className="flex items-center gap-2 mt-1"><input type="checkbox" defaultChecked /> Daily study reminder emails</label>
                </div>
                <button
                    onClick={() => { reset(); onClose(); }}
                    data-testid="settings-signout"
                    className="mono text-[11px] text-red-400 hover:underline"
                >
                    Sign out
                </button>
            </div>
        </OverlayShell>
    );
}

const HELP_ANSWERS = [
    { q: /credit|top.?up|buy/i, a: 'Credits are consumed 1 at a time for each Metis chat, lesson, or quiz. Top up from the "My Account → Pricing" hamburger.' },
    { q: /acropolis|lesson|study zone/i, a: 'The Acropolis is a distraction-free study zone. Open it from the Exam Prep chapter — pick a subject → chapter → Metis walks you through it.' },
    { q: /voice|mic/i, a: 'Tap the microphone icon in the Metis chat bubble to speak. Voice replies can be toggled from Settings.' },
    { q: /google|sign.?in|log/i, a: 'You can sign in via Google or with an email/phone. After signing in, you\'ll be asked to complete the CBSE profile if not done already.' },
    { q: /refund|cancel/i, a: 'Refund and cancellation policy is described in our Terms & Conditions (footer). In short: 7-day window, case-by-case review.' },
    { q: /.*/, a: 'I\'m here to help! Try asking about credits, the Acropolis, voice mode, sign-in, or refunds.' },
];
export function HelpChatbot({ open, onClose }) {
    const [messages, setMessages] = useState([{ role: 'bot', text: 'Hi! I\'m the Studyfolio helper. Ask me about credits, the Acropolis, voice mode, or your account.' }]);
    const [input, setInput] = useState('');
    const scrollRef = useRef(null);
    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);
    function send() {
        const t = input.trim(); if (!t) return;
        setInput('');
        setMessages((m) => [...m, { role: 'user', text: t }]);
        setTimeout(() => {
            const a = HELP_ANSWERS.find((h) => h.q.test(t))?.a || 'I can help with that shortly!';
            setMessages((m) => [...m, { role: 'bot', text: a }]);
        }, 350);
    }
    return (
        <OverlayShell open={open} onClose={onClose} title="Help Center" size="md" testId="help-chatbot">
            <div ref={scrollRef} className="max-h-[46vh] overflow-y-auto book-scroll space-y-2 mb-3">
                {messages.map((m, i) => (
                    <div key={i} className={`text-[13px] px-3 py-2 rounded-[10px] ${m.role === 'user' ? 'ml-auto max-w-[85%] bg-[#B79CFF]/25 text-[#f0f4ee]' : 'mr-auto max-w-[92%] bg-white/5 border border-[#3e4438] text-[#e6ece2]'}`} data-testid={`help-msg-${m.role}`}>
                        {m.text}
                    </div>
                ))}
            </div>
            <div className="flex items-center gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Ask about credits, voice, refunds…" className="flex-1 bg-white/5 border border-[#3e4438] rounded-sm px-3 py-2 text-[13px] text-[#f0f4ee] placeholder:text-[#8a957f] outline-none focus:border-[#B79CFF]/60" data-testid="help-input" />
                <button onClick={send} className="p-2 rounded-full bg-[color:var(--lavender-deep)] text-white" data-testid="help-send"><Send size={14} /></button>
            </div>
        </OverlayShell>
    );
}

// ---------- Smart Search (live backend autocomplete + deep links) ----------
const SEARCH_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STREAMS_PILLS = ['Engineering', 'Medicine', 'Commerce', 'Law', 'Design', 'Civil Services'];
const TRENDING = ['Quadratic Equations', 'JEE Main mock tests', 'CBSE Class 10 sample papers', 'CLAT legal reasoning'];
const GROUP_LABELS = { chapter: 'Syllabus Chapters', college: 'Colleges', exam: 'Exams' };
const PILL_KEYWORDS = {
    Engineering: ['jee', 'gate', 'iit', 'nit', 'bits', 'engineering', 'b.tech'],
    Medicine: ['neet', 'aiims', 'medical', 'mbbs', 'nursing'],
    Commerce: ['cat', 'commerce', 'economics', 'cuet', 'ca ', 'accountancy', 'business'],
    Law: ['clat', 'law', 'nlsiu', 'legal'],
    Design: ['nid', 'nift', 'design', 'architecture'],
    'Civil Services': ['upsc', 'nda', 'civil services', 'ias'],
};

export function SmartSearch({ onNavigate }) {
    const [q, setQ] = useState('');
    const [focus, setFocus] = useState(false);
    const [pill, setPill] = useState(null);
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [recent, setRecent] = useState(() => {
        try { return JSON.parse(localStorage.getItem('sf.searchRecent') || '[]'); } catch { return []; }
    });

    // Debounced live search against /api/search (Supabase syllabi + colleges + exams)
    useEffect(() => {
        const query = q.trim();
        if (query.length < 2) { setResults([]); setSearching(false); return; }
        setSearching(true);
        const t = setTimeout(async () => {
            try {
                const r = await fetch(`${SEARCH_API}/search?q=${encodeURIComponent(query)}`);
                const d = r.ok ? await r.json() : { results: [] };
                let items = d.results || [];
                if (pill) {
                    const keys = PILL_KEYWORDS[pill] || [];
                    items = items.filter((it) => keys.some((k) => `${it.title} ${it.subtitle}`.toLowerCase().includes(k)));
                }
                setResults(items);
            } catch {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [q, pill]);

    const grouped = useMemo(() => {
        const g = {};
        results.forEach((it) => { (g[it.type] = g[it.type] || []).push(it); });
        return g;
    }, [results]);

    function pick(item) {
        setRecent((r) => {
            const next = [item.title, ...r.filter((x) => x !== item.title)].slice(0, 6);
            try { localStorage.setItem('sf.searchRecent', JSON.stringify(next)); } catch {}
            return next;
        });
        // Deep link: switch section, then fire the section-specific open event.
        // Event handles the already-on-section case; the sessionStorage flag is
        // consumed by the target section on MOUNT (covers the page-flip delay).
        if (item.type === 'chapter') {
            try { sessionStorage.setItem('sf.deeplink.acropolis', JSON.stringify({ subject: item.subject, chapter: item.chapter, board: item.board, stream: item.stream })); } catch {}
            onNavigate?.('exam-prep');
            window.dispatchEvent(new CustomEvent('acropolis:open', {
                detail: { subject: item.subject, chapter: item.chapter, board: item.board, stream: item.stream },
            }));
        } else if (item.type === 'college') {
            try { sessionStorage.setItem('sf.deeplink.college', item.query || item.title); } catch {}
            onNavigate?.('home');
            window.dispatchEvent(new CustomEvent('college:prefill', {
                detail: { query: item.query || item.title },
            }));
        } else if (item.type === 'exam') {
            try { sessionStorage.setItem('sf.deeplink.exam', item.code); } catch {}
            onNavigate?.('exam-prep');
            window.dispatchEvent(new CustomEvent('exam:open', {
                detail: { code: item.code },
            }));
        }
        setQ(''); setFocus(false);
    }

    return (
        <div className="relative flex-1 max-w-xl mx-2 hidden md:block" data-testid="smart-search">
            <div className="flex items-center gap-2 bg-white/60 border border-[color:var(--moss-deep)]/30 rounded-full px-4 py-2 focus-within:border-[color:var(--lavender-deep)] transition-colors">
                <SearchIcon size={16} className="text-[color:var(--moss-deep)]" strokeWidth={2.25} />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => setFocus(true)}
                    onBlur={() => setTimeout(() => setFocus(false), 180)}
                    placeholder="Search chapters, colleges, exams…"
                    data-testid="smart-search-input"
                    className="flex-1 bg-transparent outline-none text-sm text-[color:var(--ink)] placeholder:text-[color:var(--moss-deep)]/60"
                />
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
                {STREAMS_PILLS.map((p) => (
                    <button
                        key={p}
                        onClick={() => setPill(pill === p ? null : p)}
                        data-testid={`search-pill-${p.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`mono text-[9px] px-2.5 py-1 rounded-full border transition-colors ${
                            pill === p
                                ? 'bg-[color:var(--lavender)]/60 border-[color:var(--lavender-deep)] text-[color:var(--moss-dark)]'
                                : 'border-[color:var(--moss-deep)]/30 text-[color:var(--moss-deep)] hover:bg-[color:var(--lavender)]/20'
                        }`}
                    >
                        {p}
                    </button>
                ))}
            </div>
            <AnimatePresence>
                {focus && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                        className="absolute top-full mt-2 w-full bg-[color:var(--parchment)] rounded-lg shadow-2xl border border-[color:var(--moss-deep)]/25 max-h-[60vh] overflow-y-auto book-scroll z-50"
                        data-testid="search-dropdown"
                    >
                        {q.trim().length === 0 && (
                            <div className="p-3">
                                {recent.length > 0 && (
                                    <div className="mb-3">
                                        <div className="mono text-[9px] tracking-[0.2em] text-[color:var(--moss-deep)] mb-1 flex items-center gap-1"><Clock size={10} /> RECENT</div>
                                        {recent.map((r, i) => (
                                            <button key={i} onMouseDown={() => setQ(r)} className="block text-[12px] text-[color:var(--ink)] py-1 hover:text-[color:var(--lavender-deep)]" data-testid={`search-recent-${i}`}>{r}</button>
                                        ))}
                                    </div>
                                )}
                                <div>
                                    <div className="mono text-[9px] tracking-[0.2em] text-[color:var(--moss-deep)] mb-1 flex items-center gap-1"><TrendingUp size={10} /> TRENDING</div>
                                    {TRENDING.map((t, i) => (
                                        <button key={i} onMouseDown={() => setQ(t)} className="block text-[12px] text-[color:var(--ink)] py-1 hover:text-[color:var(--lavender-deep)]" data-testid={`search-trending-${i}`}>{t}</button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {q.trim().length > 0 && q.trim().length < 2 && (
                            <div className="p-4 text-[12px] italic text-[color:var(--ink-soft)]">Keep typing — results appear after 2 characters.</div>
                        )}
                        {searching && q.trim().length >= 2 && (
                            <div className="p-4 mono text-[10px] text-[color:var(--moss-deep)]" data-testid="search-loading">Searching syllabi…</div>
                        )}
                        {!searching && q.trim().length >= 2 && Object.keys(grouped).length === 0 && (
                            <div className="p-4 text-[12px] italic text-[color:var(--ink-soft)]" data-testid="search-empty">No matches. Try another keyword.</div>
                        )}
                        {q.trim().length >= 2 && Object.entries(grouped).map(([type, items]) => (
                            <div key={type} className="border-b border-[color:var(--moss-deep)]/10 last:border-b-0">
                                <div className="mono text-[9px] tracking-[0.2em] text-[color:var(--moss-deep)] px-3 pt-2 pb-1">{(GROUP_LABELS[type] || type).toUpperCase()}</div>
                                {items.map((it, i) => (
                                    <button
                                        key={`${type}-${i}`}
                                        onMouseDown={() => pick(it)}
                                        data-testid={`search-result-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                        className="block w-full text-left px-3 py-2 hover:bg-[color:var(--lavender)]/25"
                                    >
                                        <div className="text-[13px] text-[color:var(--ink)]">{it.title}</div>
                                        <div className="mono text-[9px] text-[color:var(--moss-deep)]/80 mt-0.5">{it.subtitle}</div>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
